/**
 * `POST` / `GET /internal/wallet/credentials` - the wallet signing credentials
 * (plan 2026-09-01-1254, U2: R7, R8, KTD3).
 *
 * The three vault rows behind Apple and Google Wallet passes are written and
 * reported through this one contract. Two rules shape it:
 *
 *  - THE READ PATH CANNOT EXPRESS A SECRET. `WalletCredentialsPresenceSchema`
 *    has no field a credential could travel in, so a presence report cannot
 *    leak key material even if a future caller passed the wrong object into it.
 *    The write reply is presence-only for the same reason.
 *  - A SCHOOL-SCOPED WRITE NEEDS A NON-EMPTY `schoolPublicId`. An empty value
 *    would file a `SCHOOL/''` row, which the store's sentinel scope-ids make
 *    indistinguishable from a fleet-wide override, so the string is trimmed and
 *    length-bounded here rather than defended for the first time in the handler.
 */

import { z } from 'zod'

export const WalletProviderSchema = z.enum(['apple-wallet', 'google-wallet'])
export type WalletProvider = z.infer<typeof WalletProviderSchema>

/**
 * Apple's signing material as the operator holds it: the `.p12` Apple exports
 * and the DER `.cer` for the WWDR intermediate, both base64. The p12 stays the
 * vaulted form (KTD6) so the operator checklist never asks for a PEM conversion.
 */
export const AppleWalletCredentialsSchema = z.object({
  passTypeId: z.string().min(1).max(255),
  teamId: z.string().min(1).max(64),
  p12Base64: z.string().min(1),
  /** Apple allows an empty export password, so this is bounded but not required to be non-empty. */
  p12Password: z.string().max(255),
  wwdrCerBase64: z.string().min(1)
})
export type AppleWalletCredentials = z.infer<typeof AppleWalletCredentialsSchema>

/**
 * `serviceAccountJson` stays a STRING, not a parsed object: it is the file the
 * operator downloaded, and re-serializing a parsed copy would rewrite the
 * newline escaping inside `private_key`. The write path parses it to check the
 * two fields the mint needs, and stores the original text.
 */
export const GoogleWalletCredentialsSchema = z.object({
  issuerId: z.string().min(1).max(64),
  serviceAccountJson: z.string().min(1)
})
export type GoogleWalletCredentials = z.infer<typeof GoogleWalletCredentialsSchema>

/** The link-signing secret row (label = key id). Never written through this API. */
export const WalletLinkCredentialsSchema = z.object({
  secret: z.string().min(1)
})
export type WalletLinkCredentials = z.infer<typeof WalletLinkCredentialsSchema>

const walletCredentialsWriteBase = {
  /**
   * Omit for the PLATFORM row (the fleet-wide default). Supply a school's
   * `publicId` to file a SCHOOL-scope override. Trimmed and non-empty: see the
   * header.
   */
  schoolPublicId: z.string().trim().min(1).max(64).optional(),
  /**
   * Defaults to FALSE. A filed wallet certificate is the one an issued pass was
   * signed against, so replacing it is an explicit decision, exactly as it is
   * for an App Store Connect key.
   */
  allowOverwrite: z.boolean().optional().default(false)
}

export const WalletCredentialsWriteRequestSchema = z.discriminatedUnion('provider', [
  z.object({
    provider: z.literal('apple-wallet'),
    ...walletCredentialsWriteBase,
    credentials: AppleWalletCredentialsSchema
  }),
  z.object({
    provider: z.literal('google-wallet'),
    ...walletCredentialsWriteBase,
    credentials: GoogleWalletCredentialsSchema
  })
])
export type WalletCredentialsWriteRequest = z.infer<typeof WalletCredentialsWriteRequestSchema>

export const WalletCredentialsPresenceQuerySchema = z.object({
  schoolPublicId: z.string().trim().max(64).optional()
})
export type WalletCredentialsPresenceQuery = z.infer<typeof WalletCredentialsPresenceQuerySchema>

/** ISO-8601 strings, because this crosses HTTP. */
const AppleCredentialStatusSchema = z.object({
  present: z.boolean(),
  isActive: z.boolean(),
  /** The signing certificate's expiry, read off the non-secret `config` written at deposit time. */
  notAfter: z.string().optional(),
  storedAt: z.string().optional()
})

const GoogleCredentialStatusSchema = z.object({
  present: z.boolean(),
  isActive: z.boolean(),
  /** The service account's address. Public by nature - it is the JWT issuer. */
  clientEmail: z.string().optional(),
  storedAt: z.string().optional()
})

export const WalletCredentialsPresenceSchema = z.object({
  apple: AppleCredentialStatusSchema,
  google: GoogleCredentialStatusSchema,
  /** The link-signing secret. `keyId` names the row minting is configured against. */
  links: z.object({ present: z.boolean(), keyId: z.string() })
})
export type WalletCredentialsPresence = z.infer<typeof WalletCredentialsPresenceSchema>

export const WalletCredentialsPresenceResponseSchema = z.object({
  success: z.literal(true),
  data: WalletCredentialsPresenceSchema
})
export type WalletCredentialsPresenceResponse = z.infer<typeof WalletCredentialsPresenceResponseSchema>

export const WalletCredentialsWriteResponseSchema = z.object({
  success: z.literal(true),
  data: z.object({
    provider: WalletProviderSchema,
    scope: z.enum(['PLATFORM', 'SCHOOL']),
    schoolPublicId: z.string().optional(),
    /** Whether an existing row was replaced (only reachable with `allowOverwrite`). */
    replaced: z.boolean(),
    /** The `wallet-links` key id this deposit made sure exists. */
    linkKeyId: z.string()
  }),
  message: z.string()
})
export type WalletCredentialsWriteResponse = z.infer<typeof WalletCredentialsWriteResponseSchema>
